import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withCustomValidation } from '@bingads-webui-react/validation';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import {
  MultiSelectorContextProvider,
  makeUseOptionsAsyncDataSource,
  makeUseSelectedOptionsAsyncDataSource,
} from '@bingads-webui-campaign-react/multi-selector';
import { NewCustomerAcquisitionImpl } from './new-customer-acquisition-impl';
import { loadAudiencesFactory, loadAssociatedAudiencesFactory } from './data-source';

const DEFAULT_OPTION_DISPLAY_LIMIT = 20;
const DEFAULT_SELECTED_OPTIONS_LIMIT = 5;

const NewCustomerAcquisition = ({
  cid,
  aid,
  bidStrategy,
  optOut,
  validationProps,
  updateValidation,
}) => {
  const activity = useContext(ActivityContext);
  const i18n = useContext(I18nContext);
  const { data: { newCustomerAcquisition } } = validationProps;

  const loadAudiences = useMemo(() => loadAudiencesFactory(cid, aid), [cid, aid]);
  const useOptions = useMemo(() => makeUseOptionsAsyncDataSource(loadAudiences), [loadAudiences]);

  const loadAssociatedAudiences = useMemo(() => loadAssociatedAudiencesFactory(cid, aid), [cid, aid]);
  const useSelectedOptions = useMemo(() => makeUseSelectedOptionsAsyncDataSource(loadAssociatedAudiences), [loadAssociatedAudiences]);

  const onChange = useCallback((newData, errorMessages) => {
    updateValidation(errorMessages, newData);
    if (activity) {
      activity.signal(`onNewCustomerAcquisitionChange - ${JSON.stringify(newData)}`);
    }
  }, [updateValidation, activity]);

  return (
    <MultiSelectorContextProvider
      useOptions={useOptions}
      useSelectedOptions={useSelectedOptions}
      selectedOptionsLimit={DEFAULT_SELECTED_OPTIONS_LIMIT}
      optionsDisplayLimit={DEFAULT_OPTION_DISPLAY_LIMIT}
    >
      <NewCustomerAcquisitionImpl
        newCustomerAcquisition={newCustomerAcquisition}
        cid={cid}
        aid={aid}
        bidStrategy={bidStrategy}
        optOut={optOut}
        i18n={i18n}
        onChange={onChange}
      />
    </MultiSelectorContextProvider>
  );
};

NewCustomerAcquisition.propTypes = {
  cid: PropTypes.number.isRequired,
  aid: PropTypes.number.isRequired,
  bidStrategy: PropTypes.string.isRequired,
  optOut: PropTypes.bool.isRequired,
  validationProps: PropTypes.shape({
    data: PropTypes.shape({
      newCustomerAcquisition: PropTypes.shape({
        campaignSettings: PropTypes.shape({
          additionalConversionValue: PropTypes.number,
          bidOnlyMode: PropTypes.bool,
          goalId: PropTypes.number,
        }),
        accountSettings: PropTypes.shape({
          additionalConversionValue: PropTypes.number,
          audiences: PropTypes.arrayOf({
            Id: PropTypes.number,
            Name: PropTypes.string,
          }),
          goalId: PropTypes.number,
        }),
      }),
    }),
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
};

export const ValidatedNewCustomerAcquisition = withCustomValidation(NewCustomerAcquisition);
